import api from '../utils/Api';

// Función genérica para manejar peticiones y errores
const handleRequest = async (requestFn, errorMsg) => {
  try {
    const res = await requestFn();
    // Si tu wrapper Api ya devuelve res.data, res será ese objeto.
    // Si devuelve axios response, res.data contendrá el body.
    const data = res && res.data ? res.data : res;
    return data ?? {};
  } catch (err) {
    // Log claro para debugging
    console.error('[enviosService] ❌', err.response?.data || err.message || err);
    return {
      success: false,
      mensaje: errorMsg || err?.response?.data?.mensaje || err.message || 'Error en la petición',
      detalle: err?.response?.data || null
    };
  }
};

// Utilidad para normalizar envíos (devuelve los campos que usa la UI)
const normalizeEnvio = (e) => {
  if (!e) return null;
  const id = String(e._id || e.id || e.envioId || '');
  return {
    id,
    numeroGuia: e.numeroGuia || e.numero_guia || e.guia || '',
    descripcion: e.descripcion || '',
    origen: e.origen || '',
    destino: e.destino || '',
    tipoEnvio: e.tipoEnvio || e.tipo || '',
    estado: e.estado || '',
    pesoLibras: typeof e.pesoLibras !== 'undefined' ? Number(e.pesoLibras) : null,
    casilleroCodigo: e.casilleroCodigo || e.casillero || '',
    userId: e.userId ? String(e.userId) : (e.usuarioId ? String(e.usuarioId) : undefined),
    colaboradorId: e.colaboradorId ? String(e.colaboradorId) : undefined,
    createdAt: e.createdAt || e.fechaCreacion || null,
    // preserve raw object for edge cases
    _raw: e
  };
};

/**
 * 🔹 Listar todos los envíos (solo admin/superadmin)
 * params: { page, limit } (opcionales)
 */
export const getAllEnvios = async ({ page = 1, limit = 20 } = {}) => {
  const query = `?page=${Number(page)}&limit=${Number(limit)}`;
  const data = await handleRequest(() => api.get(`/envios/all${query}`), '❌ Error al obtener todos los envíos');
  return {
    success: !!data.success,
    mensaje: data.mensaje || 'Listado de envíos',
    page: data.page || Number(page),
    limit: data.limit || Number(limit),
    total: data.total || (Array.isArray(data.envios) ? data.envios.length : 0),
    envios: Array.isArray(data.envios) ? data.envios.map(normalizeEnvio) : []
  };
};

/**
 * 🔹 Listar envíos del cliente autenticado
 * params: { page, limit } (opcionales)
 */
export const getMisEnvios = async ({ page = 1, limit = 50 } = {}) => {
  const query = `?page=${Number(page)}&limit=${Number(limit)}`;
  // ruta: /envios/mis-envios (según listar.js)
  const data = await handleRequest(() => api.get(`/envios/mis-envios${query}`), '❌ Error al obtener mis envíos');
  return {
    success: !!data.success,
    mensaje: data.mensaje || 'Listado de envíos del cliente',
    page: data.page || Number(page),
    limit: data.limit || Number(limit),
    total: data.total || (Array.isArray(data.envios) ? data.envios.length : 0),
    envios: Array.isArray(data.envios) ? data.envios.map(normalizeEnvio) : []
  };
};

/**
 * 🔹 Obtener envíos por tipo (admin/superadmin)
 * params: tipo (string), options: { page, limit }
 */
export const getEnviosPorTipo = async (tipo, { page = 1, limit = 20 } = {}) => {
  if (!tipo) {
    return { success: false, mensaje: 'Tipo de envío es obligatorio', envios: [], total: 0 };
  }
  const tipoEnc = encodeURIComponent(tipo);
  const query = `?page=${Number(page)}&limit=${Number(limit)}`;
  const data = await handleRequest(() => api.get(`/envios/tipo/${tipoEnc}${query}`), '❌ Error al obtener envíos por tipo');
  return {
    success: !!data.success,
    mensaje: data.mensaje || `Envíos por tipo: ${tipo}`,
    page: data.page || Number(page),
    limit: data.limit || Number(limit),
    total: data.total || (Array.isArray(data.envios) ? data.envios.length : 0),
    envios: Array.isArray(data.envios) ? data.envios.map(normalizeEnvio) : []
  };
};

/**
 * 🔹 Obtener envíos por casillero (admin o cliente)
 * params: codigo (string)
 */
export const getEnviosPorCasillero = async (codigo) => {
  if (!codigo) {
    return { success: false, mensaje: 'Código de casillero es obligatorio', envios: [], total: 0 };
  }
  // Mantener la normalización que ya usas en el backend; backend hace búsqueda case-insensitive
  const codigoEnc = encodeURIComponent(String(codigo).trim());
  const data = await handleRequest(() => api.get(`/envios/casillero/${codigoEnc}`), '❌ Error al obtener envíos por casillero');
  return {
    success: !!data.success,
    mensaje: data.mensaje || `Envíos del casillero ${codigo}`,
    total: data.total || (Array.isArray(data.envios) ? data.envios.length : 0),
    envios: Array.isArray(data.envios) ? data.envios.map(normalizeEnvio) : []
  };
};

/**
 * 🔹 Obtener historial de un envío
 */
export const getHistorial = async (id) => {
  if (!id) return { success: false, mensaje: 'ID es obligatorio', historial: [] };
  const data = await handleRequest(() => api.get(`/envios/historial/${encodeURIComponent(id)}`), '❌ Error al obtener historial');
  return {
    success: !!data.success,
    mensaje: data.mensaje || 'Historial obtenido',
    historial: Array.isArray(data.historial) ? data.historial : []
  };
};

/**
 * 🔹 Consultar envío por número de guía
 */
export const consultarPorGuia = async (guia) => {
  if (!guia) return { success: false, mensaje: 'Número de guía es obligatorio', envio: null };
  const data = await handleRequest(() => api.get(`/envios/buscar/${encodeURIComponent(guia)}`), '❌ Error al consultar envío');
  return {
    success: !!data.success,
    mensaje: data.mensaje || 'Consulta realizada',
    envio: data.envio ? normalizeEnvio(data.envio) : null
  };
};

/**
 * 🔹 Crear un nuevo envío
 */
export const crearEnvio = async (payload) => {
  const camposObligatorios = ['numeroGuia', 'descripcion', 'origen', 'destino', 'tipoEnvio', 'casilleroCodigo', 'pesoLibras'];
  for (const campo of camposObligatorios) {
    if (!payload?.[campo] && payload?.[campo] !== 0) {
      return { success: false, mensaje: `El campo ${campo} es obligatorio` };
    }
  }
  if (Number(payload.pesoLibras) <= 0) {
    return { success: false, mensaje: 'Debe ingresar un peso válido en libras' };
  }

  const envioPayload = {
    ...payload,
    tipoEnvio: String(payload.tipoEnvio || '').trim().toLowerCase(),
    casilleroCodigo: String(payload.casilleroCodigo || '').trim().toLowerCase(),
    pesoLibras: Number(payload.pesoLibras)
  };

  const data = await handleRequest(() => api.post('/envios/crear', envioPayload), '❌ Error al crear envío');

  // backend puede devolver data.envios, data.envio o data.envioId
  const envioRaw = data?.envio || (Array.isArray(data.envios) && data.envios[0]) || null;
  const id = envioRaw ? (envioRaw._id || envioRaw.id || envioRaw.envioId) : (data?.envioId || null);

  if (!id && !envioRaw) {
    return { success: false, mensaje: data.mensaje || 'El backend no devolvió un identificador de envío' };
  }

  const envioNormalizado = normalizeEnvio(envioRaw ? envioRaw : { ...data.envio, id });

  return {
    success: true,
    mensaje: data.mensaje || 'Envío creado correctamente',
    envio: envioNormalizado
  };
};

/**
 * 🔹 Actualizar estado de un envío
 */
export const actualizarEstado = async (id, estado) => {
  if (!id || !estado) {
    return { success: false, mensaje: 'ID y estado son obligatorios' };
  }

  const data = await handleRequest(() => api.put(`/envios/actualizar/estado/${encodeURIComponent(id)}`, { estado }), '❌ Error al actualizar estado');

  return {
    success: !!data.success,
    mensaje: data.mensaje || 'Estado actualizado',
    envio: data.envio ? normalizeEnvio(data.envio) : null
  };
};

/**
 * 🔹 Obtener lista de tipos de envío (backend opcional; fallback local)
 */
export const getTiposEnvio = async () => {
  try {
    const res = await handleRequest(() => api.get('/envios/tipos'), '❌ Error al obtener tipos de envío');
    if (res && Array.isArray(res.tipos) && res.tipos.length) return res.tipos;
  } catch (e) {
    // fallback abajo
  }
  // Fallback seguro y consistente con backend
  return ['aereo', 'terrestre', 'maritimo'];
};